import React, { useEffect, useRef, useState } from "react";
import { askRefereeFn } from "../firebase";
import { RefereeMark } from "../SportIcons";
import { isNative } from "../nativePush";

/**
 * Selaimen oma puheentunnistus. Toimii Chromessa ja tyopoydan
 * Safarissa, mutta EI naissa kahdessa:
 *
 * 1. WKWebView eli Capacitorin natiiviappi.
 * 2. iOS:n kotivalikkoon asennettu PWA. Tama on se ansa: WebKit EI
 *    tue puheentunnistusta kotivalikon sovelluksessa, mutta
 *    `webkitSpeechRecognition` on silti olemassa. Olemassaolon
 *    tarkistus menee lapi, nappi nakyy, `start()` ei heita — eika
 *    mitaan tapahdu. Juuri niin se epaonnistui.
 *
 * Siksi nappi piilotetaan molemmissa, ja tilalle tulee vihje
 * nappaimiston omasta sanelusta, joka toimii <textarea>ssa aina.
 * Natiivin mikrofoni vaatii oman liitannaisen (docs/ideat.md H3).
 */
type SpeechCtor = new () => {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  start: () => void;
  stop: () => void;
};
const SpeechRec: SpeechCtor | undefined =
  typeof window === "undefined" ? undefined :
    ((window as unknown as Record<string, SpeechCtor | undefined>).SpeechRecognition ??
     (window as unknown as Record<string, SpeechCtor | undefined>).webkitSpeechRecognition);

/**
 * Onko tama iOS:n kotivalikosta avattu PWA.
 *
 * `navigator.standalone` on iOS:n oma lippu; display-mode kattaa myos
 * uudemmat. iPadOS ilmoittautuu Maciksi, joten se tunnistetaan
 * kosketuspisteista.
 */
const iosHomeScreen = (() => {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const nav = navigator as Navigator & { standalone?: boolean; maxTouchPoints?: number };
  const standalone = nav.standalone === true ||
    window.matchMedia?.("(display-mode: standalone)").matches === true;
  const ios = /iPad|iPhone|iPod/.test(nav.userAgent) ||
    (nav.platform === "MacIntel" && (nav.maxTouchPoints ?? 0) > 1);
  return standalone && ios;
})();

/**
 * Kuinka kauan tunnistuksen kaynnistymista odotetaan.
 *
 * Vahti valvoo nimenomaan kaynnistymista eika koko kuuntelua: `onstart`
 * sammuttaa sen. Jos se valvoisi koko kuuntelua, hiljaa mietiskeleva
 * kysyja saisi virheilmoituksen keskelle toimivaa sanelua. iOS:n
 * kotivalikon PWA:ssa `onstart` ei tule koskaan, ja juuri se on se tila
 * joka pitaa huomata.
 */
const MIC_START_TIMEOUT_MS = 4000;

/**
 * Puheentunnistuksen virhekoodi suomeksi ja englanniksi.
 *
 * Ennen `onerror` vain sammutti kuuntelun eika kertonut mitaan, joten
 * estetty mikrofoni, puuttuva verkko ja hiljaisuus nayttivat kaikki
 * samalta: nappia painaa, mitaan ei tapahdu.
 *
 * @param {string} code SpeechRecognitionErrorEventin error-kentta.
 * @param {boolean} fi Onko kieli suomi.
 * @return {string} Selittava virheilmoitus.
 */
function micError(code: string | undefined, fi: boolean): string {
  switch (code) {
    case "not-allowed":
    case "service-not-allowed":
      return fi
        ? "Mikrofonin käyttö on estetty. Salli mikrofoni selaimen asetuksista, tai sanele näppäimistön mikrofonilla."
        : "Microphone access is blocked. Allow it in browser settings, or dictate with the keyboard mic.";
    case "no-speech":
      return fi ? "En kuullut mitään. Yritä uudestaan." : "I heard nothing. Try again.";
    case "audio-capture":
      return fi ? "Mikrofonia ei löytynyt." : "No microphone found.";
    case "network":
      return fi
        ? "Puheentunnistus ei saanut verkkoyhteyttä."
        : "Speech recognition could not reach the network.";
    case "aborted":
      return "";
    default:
      return fi
        ? "Puheentunnistus ei onnistunut tässä selaimessa. Kirjoita kysymys tai sanele näppäimistön mikrofonilla."
        : "Speech recognition failed in this browser. Type the question or use the keyboard mic.";
  }
}

/**
 * Kysy tuomarilta (docs/ideat.md H2).
 *
 * Avautuu Tuomari-napista. Kysymyskenttä on tavallinen <textarea>, joten
 * puhelimen näppäimistön sanelunappi toimii siinä sellaisenaan — oma
 * mikrofoni on H3 eikä sitä tarvita tähän.
 */
export default function RefereeAsk({ lang, onClose }: {
  lang: "fi" | "en";
  onClose: () => void;
}) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer]     = useState("");
  const [error, setError]       = useState("");
  const [busy, setBusy]         = useState(false);
  const [listening, setListening] = useState(false);
  const recRef = useRef<InstanceType<SpeechCtor> | null>(null);
  const watchdog = useRef<number | null>(null);
  const micAvailable = !!SpeechRec && !isNative() && !iosHomeScreen;

  const fi = lang === "fi";

  async function ask() {
    const q = question.trim();
    if (!q || busy) return;
    setBusy(true); setError(""); setAnswer("");
    try {
      const res = await askRefereeFn({ question: q });
      setAnswer(res.data.answer);
    } catch (e) {
      // Palvelin erottaa kiintiön, oikeuden ja häiriön toisistaan, ja
      // sen viesti on tarkempi kuin mikään arvaus täällä.
      const msg = (e as { message?: string })?.message;
      setError(msg || (fi ? "Tuomari ei vastannut." : "No answer."));
    }
    setBusy(false);
  }

  // Kuuntelu lopetetaan jos komponentti suljetaan kesken: muuten
  // tunnistus jaa paalle ja mikrofoni nayttaa olevan kaytossa.
  useEffect(() => () => {
    if (watchdog.current) window.clearTimeout(watchdog.current);
    try { recRef.current?.stop(); } catch { /* jo lopetettu */ }
  }, []);

  // Sanelu liitetaan kentan perään eika korvaa sita: kysymysta voi
  // jatkaa puheella tai kirjoittamalla kumminpain vain.
  function toggleMic() {
    if (listening) { stopMic(); return; }
    if (!SpeechRec) return;
    setError("");
    const rec = new SpeechRec();
    rec.lang = fi ? "fi-FI" : "en-US";
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = e => {
      const heard = Array.from(e.results)
        .map(r => r[0].transcript)
        .join(" ")
        .trim();
      if (heard) setQuestion(q => (q.trim() ? q.trim() + " " : "") + heard);
    };
    rec.onstart = () => clearWatchdog();
    rec.onend = () => { clearWatchdog(); setListening(false); };
    rec.onerror = e => {
      clearWatchdog();
      setListening(false);
      const msg = micError(e?.error, fi);
      if (msg) setError(msg);
    };
    recRef.current = rec;
    setListening(true);
    // Jos tunnistus ei kaynnisty lainkaan — kuten iOS:n kotivalikon
    // PWA:ssa — mikaan tapahtuma ei kerro siita. Ilman tata vahtia
    // nappi jaisi punaisena loputtomiin.
    watchdog.current = window.setTimeout(() => {
      stopMic();
      setError(fi
        ? "Puheentunnistus ei käynnistynyt. Sanele näppäimistön mikrofonilla tai kirjoita kysymys."
        : "Speech recognition did not start. Use the keyboard mic or type the question.");
    }, MIC_START_TIMEOUT_MS);
    try { rec.start(); } catch { clearWatchdog(); setListening(false); setError(micError(undefined, fi)); }
  }

  function clearWatchdog() {
    if (watchdog.current) { window.clearTimeout(watchdog.current); watchdog.current = null; }
  }

  function stopMic() {
    clearWatchdog();
    setListening(false);
    try { recRef.current?.stop(); } catch { /* jo lopetettu */ }
  }

  const card: React.CSSProperties = {
    background: "var(--sl-card, #fff)", border: "1px solid var(--sl-border)",
    borderRadius: 14, padding: "14px 16px", marginBottom: 12,
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 10000, background: "var(--sl-bg)",
      overflowY: "auto", fontFamily: "'Inter', sans-serif",
      paddingTop: "env(safe-area-inset-top, 0px)",
      paddingBottom: "calc(24px + var(--sl-safe-bottom))",
    }}>
      <div style={{ maxWidth: 520, margin: "0 auto", padding: "16px 16px 0" }}>
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between", marginBottom: 14,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <RefereeMark size={40} />
            <div style={{ fontSize: 20, fontWeight: 800, color: "var(--sl-text)" }}>
              {fi ? "Kysy tuomarilta" : "Ask the referee"}
            </div>
          </div>
          <button onClick={onClose} aria-label={fi ? "Sulje" : "Close"} style={{
            border: "none", background: "var(--sl-soft)", color: "var(--sl-text)",
            borderRadius: 10, padding: "8px 14px", fontSize: 14,
            fontWeight: 700, cursor: "pointer",
          }}>
            ✕
          </button>
        </div>

        <div style={card}>
          <textarea
            value={question}
            onChange={e => setQuestion(e.target.value)}
            maxLength={400}
            rows={3}
            autoFocus
            // Kolme esimerkkia kolmesta eri kerroksesta: saanto, klubin
            // kaytanto, illan tieto. Yksi saantoesimerkki opetti kysymaan
            // vain saantoja, eika silloin olisi koskaan selvinnyt mihin
            // tata oikeasti halutaan kayttaa.
            placeholder={fi
              ? "Esim. saako keittiössä lyödä ilmasta?\nMilloin sali menee kiinni?\nPaljonko panos on tänään?"
              : "E.g. may I volley in the kitchen?\nWhen does the hall close?\nWhat is tonight's stake?"}
            style={{
              width: "100%", boxSizing: "border-box", padding: "11px 13px",
              background: "var(--sl-bg)", border: "1.5px solid var(--sl-border)",
              borderRadius: 10, fontSize: 14, color: "var(--sl-text)",
              fontFamily: "'Inter', sans-serif", outline: "none", resize: "vertical",
            }}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            {micAvailable && (
              <button
                onClick={toggleMic}
                aria-label={fi ? "Sanele kysymys" : "Dictate question"}
                style={{
                  padding: "12px 16px", borderRadius: 10, border: "none",
                  fontSize: 18, cursor: "pointer", lineHeight: 1,
                  background: listening ? "var(--sl-error)" : "var(--sl-soft)",
                  color: listening ? "#fff" : "var(--sl-text)",
                }}
              >
                {listening ? "■" : "🎤"}
              </button>
            )}
            <button onClick={ask} disabled={busy || !question.trim()} style={{
              flex: 1, padding: "12px 0", borderRadius: 10,
              border: "none", fontSize: 14, fontWeight: 700,
              cursor: busy || !question.trim() ? "default" : "pointer",
              background: "var(--sl-primary)", color: "#fff",
              opacity: busy || !question.trim() ? 0.55 : 1,
            }}>
              {busy
                ? (fi ? "Kysytään…" : "Asking…")
                : (fi ? "Kysy" : "Ask")}
            </button>
          </div>
          {listening && (
            <div style={{ fontSize: 12, color: "var(--sl-muted)", marginTop: 8 }}>
              {fi ? "Kuuntelen… puhu ja lopeta painamalla ■" : "Listening… speak, then press ■"}
            </div>
          )}
          {/* Nappaimiston sanelu toimii kentassa aina, myos siella missa
              selaimen puheentunnistus ei ole kaytettavissa. Ilman tata
              vihjetta puuttuva mikkinappi nayttaa puuttuvalta
              ominaisuudelta. */}
          {!micAvailable && (
            <div style={{ fontSize: 12, color: "var(--sl-muted)", marginTop: 8, lineHeight: 1.5 }}>
              {fi
                ? "Voit sanella kysymyksen näppäimistön mikrofonilla 🎤 — avaa näppäimistö ja paina sitä."
                : "You can dictate with the keyboard's microphone 🎤 — open the keyboard and tap it."}
            </div>
          )}
        </div>

        {error && (
          <div style={{ ...card, borderColor: "var(--sl-error)" }}>
            <div style={{ fontSize: 13, color: "var(--sl-error)", lineHeight: 1.5 }}>
              {error}
            </div>
          </div>
        )}

        {answer && (
          <div style={card}>
            <div style={{
              fontSize: 14, lineHeight: 1.6, color: "var(--sl-text)",
              whiteSpace: "pre-wrap",
            }}>
              {answer}
            </div>
          </div>
        )}

        <div style={{
          fontSize: 12, color: "var(--sl-muted)", lineHeight: 1.55,
          margin: "4px 2px 8px",
        }}>
          {fi
            ? "Tuomari neuvoo virallisten sääntöjen, klubin sääntöjen ja illan sopimusten pohjalta. Se ei ole virallinen päätös."
            : "The referee advises based on the official rules, your club's rules and tonight's agreement. It is not an official ruling."}
        </div>
      </div>
    </div>
  );
}
